import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useToaster } from 'src/utils/toaster/toasterContext';
import DMTemplateConfiguration from 'src/pages/auto-dm/create-automation-trigger/DMTemplateConfiguration';
import { useTranslation } from 'react-i18next';

const TriggerConfigDialog = ({ open, onClose, onSave, profile, type }) => {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const [formData, setFormData] = useState({
    trigger_type: type || 'comment',
    trigger_values: [],
    any_trigger_value: false,
    dm_message: '',
    comment_reply: '',
    dm_template: null,
    is_dm_template: false,
  });
  const [inputTriggerValue, setInputTriggerValue] = useState('');

  useEffect(() => {
    if (profile) {
      setFormData({
        trigger_type: profile.automation_trigger?.trigger_type || type || 'comment',
        trigger_values:
          profile.automation_trigger?.trigger_values ||
          (profile.automation_trigger?.trigger_value
            ? profile.automation_trigger.trigger_value.split(',')
            : []),
        any_trigger_value: profile.automation_trigger?.any_trigger_value || false,
        dm_message: profile.automation_trigger?.dm_message || '',
        comment_reply: profile.automation_trigger?.comment_reply || '',
        dm_template: profile.automation_trigger?.dm_template || null,
        is_dm_template: profile.automation_trigger?.is_dm_template || false,
      });
    }
  }, [profile, type]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddTriggerValue = () => {
    if (inputTriggerValue.trim()) {
      setFormData((prev) => ({
        ...prev,
        trigger_values: [...prev.trigger_values, inputTriggerValue.trim()],
      }));
      setInputTriggerValue('');
    }
  };

  const handleDeleteTriggerValue = (valueToDelete) => {
    setFormData((prev) => ({
      ...prev,
      trigger_values: prev.trigger_values.filter((value) => value !== valueToDelete),
    }));
  };

  const handleAnyTriggerValueToggle = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      any_trigger_value: checked,
      trigger_values: checked ? [] : prev.trigger_values,
    }));
  };

  const handleTemplateChange = (newTemplate) => {
    setFormData((prev) => ({ ...prev, dm_template: newTemplate }));
  };

  const handleTriggerTypeChange = (e) => {
    const newType = e.target.value;
    setFormData((prev) => ({
      ...prev,
      trigger_type: newType,
      comment_reply: newType === 'comment' ? prev.comment_reply : '',
      trigger_values: newType === 'like' && prev.any_trigger_value ? [] : prev.trigger_values,
      any_trigger_value: newType === 'like' ? true : prev.any_trigger_value,
    }));
  };

  const handleDmTemplateToggle = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      is_dm_template: checked,
      dm_message: checked ? '' : prev.dm_message,
      dm_template: checked
        ? prev.dm_template || {
            title: '',
            subtitle: '',
            image_url: '',
            default_action: { type: 'web_url', url: '' },
            buttons: [],
          }
        : null,
    }));
  };

  const validateForm = () => {
    if (!formData.trigger_type) {
      showToast('Trigger type is required', 'e');
      return false;
    }
    if (!formData.any_trigger_value && formData.trigger_values.length === 0 && !inputTriggerValue.trim()) {
      showToast('At least one trigger value is required unless any trigger value is enabled', 'e');
      return false;
    }
    if ( formData.any_trigger_value !== 'like' && formData.trigger_values.length === 0 && inputTriggerValue.trim()) {
      showToast('Press Enter to add the trigger value to the list', 'e');
      return;
    }
    if (formData.trigger_type === 'like') {
      if (formData.is_dm_template) {
        if (!formData.dm_template?.title?.trim()) {
          showToast('Template title is required for like trigger', 'e');
          return false;
        }
        if (formData.dm_template?.buttons?.length > 3) {
          showToast('Maximum 3 buttons allowed in template', 'e');
          return false;
        }
      } else {
        if (!formData.dm_message.trim()) {
          showToast('DM Message is required for like trigger', 'e');
          return false;
        }
      }
    }
    if (formData.trigger_type === 'comment') {
      if (formData.is_dm_template) {
        if (!formData.dm_template?.title?.trim() && !formData.comment_reply.trim()) {
          showToast('Template title or Comment Reply is required for comment trigger', 'e');
          return false;
        }
        if (formData.dm_template?.buttons?.length > 3) {
          showToast('Maximum 3 buttons allowed in template', 'e');
          return false;
        }
      } else {
        if (!formData.dm_message.trim() && !formData.comment_reply.trim()) {
          showToast('At least one of DM Message or Comment Reply is required for comment trigger', 'e');
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{translate('trigger_config.configure_auto_dms', 'Configure Auto DMs')}</DialogTitle>
      <DialogContent sx={{ padding: 3 }}>
        <Grid container spacing={2} sx={{ marginTop: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>{translate('trigger_config.trigger_type', 'Trigger Type')}</InputLabel>
              <Select value={formData.trigger_type} onChange={handleTriggerTypeChange} label={translate('trigger_config.trigger_type', 'Trigger Type')}>
                <MenuItem value="comment">{translate('trigger_config.comment', 'Comment')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {formData.trigger_type !== 'like' && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.any_trigger_value}
                    onChange={handleAnyTriggerValueToggle}
                    color="primary"
                  />
                }
                label={translate('trigger_config.accept_any_trigger_value', 'Accept Any Trigger Value')}
                sx={{ mb: 1 }}
              />
            </Grid>
          )}
          {!formData.any_trigger_value && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={translate('trigger_config.trigger_values', 'Trigger Values')}
                value={inputTriggerValue}
                onChange={(e) => setInputTriggerValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTriggerValue()}
                placeholder={translate('trigger_config.enter_trigger_value', 'Enter trigger value and press Enter')}
                variant="outlined"
                size="small"
                error={formData.trigger_values.length === 0}
                helperText={formData.trigger_values.length === 0 ? translate('trigger_config.at_least_one_trigger_value_required', 'At least one trigger value required') : ''}
              />
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.trigger_values.map((value, index) => (
                  <Chip
                    key={index}
                    label={value}
                    onDelete={() => handleDeleteTriggerValue(value)}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={formData.is_dm_template} onChange={handleDmTemplateToggle} color="primary" />}
              label={translate('trigger_config.use_generic_template_dm', 'Use Generic Template DM')}
              sx={{ mb: 1 }}
            />
          </Grid>
          {!formData.is_dm_template && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={translate('trigger_config.dm_message', 'DM Message')}
                name="dm_message"
                value={formData.dm_message}
                onChange={handleChange}
                multiline
                rows={2}
                variant="outlined"
                size="small"
                error={formData.trigger_type === 'like' && !formData.dm_message.trim()}
                helperText={formData.trigger_type === 'like' && !formData.dm_message.trim() ? translate('trigger_config.required', 'Required') : ''}
              />
            </Grid>
          )}
          {formData.is_dm_template && (
            <Grid item xs={12}>
              <DMTemplateConfiguration
                template={
                  formData.dm_template || {
                    title: '',
                    subtitle: '',
                    image_url: '',
                    default_action: { type: 'web_url', url: '' },
                    buttons: [],
                  }
                }
                onChange={handleTemplateChange}
              />
            </Grid>
          )}
          {formData.trigger_type === 'comment' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={translate('trigger_config.comment_reply', 'Comment Reply')}
                name="comment_reply"
                value={formData.comment_reply}
                onChange={handleChange}
                multiline
                rows={2}
                variant="outlined"
                size="small"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Button onClick={onClose} variant="outlined" color="error">
          {translate('trigger_config.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!formData.trigger_type}
          sx={{ ml: 1 }}
        >
          {translate('trigger_config.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TriggerConfigDialog;